import { TabContext, TabPanel } from "@mui/lab";
import { Paper, Tab, Tabs, Tooltip, tabClasses, tabsClasses } from "@mui/material";
import Box from "@mui/material/Box";
import { styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import { middleEllipsisTrim } from "@utils";
import { MandatoryBadge } from "./MandatoryBadge";
import palette from "src/theme/palette";

const TabsBox = styled(Box)(({ borderColor }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: `1px solid ${borderColor}`
}));

const HyroTabs = ({
  headerComponent,
  tabsSpec,
  trimLabel = false,
  borderColor = palette.system[300],
  tabsSideComponent = null,
  selectedValue,
  customTabHeight,
  onTabChange,
  controlled = false,
  panelStyle,
  tabsStyle,
  tabsBoxStyle }) => {
  const [activeTab, setActiveTab] = useState(tabsSpec[0].value);

  const resolvedSelected = (tabsSpec.find(({ value }) => value?.toLowerCase() === selectedValue?.toLowerCase())?.value)
    ?? tabsSpec[0].value;
  const activeValue = controlled ? resolvedSelected : activeTab;

  useEffect(() => {
    if (controlled) return;
    let { value } = tabsSpec[0] || {};

    if (selectedValue) {
      value = tabsSpec.find(({ value }) => value?.toLowerCase() === selectedValue?.toLowerCase())?.value;
      value = value ?? tabsSpec[0].value;
    }
    setActiveTab(value);

  }, [selectedValue]);

  const handleChange = (event, newActiveTab) => {
    if (!controlled) setActiveTab(newActiveTab);
    onTabChange?.(newActiveTab);
  };

  return (
    <>
      {headerComponent}
      <TabContext value={activeValue}>
        <TabsBox borderColor={borderColor} sx={tabsBoxStyle}>
          <Tabs
            sx={{
              minHeight: "36px",
              [`& .${tabsClasses.list}`]: {
                gap: 2
              },
              ...tabsStyle
            }}
            value={activeValue}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabsSpec.map(({ label, value, disabled, mandatory, tabStyle }) => (
              <Tab
                sx={{
                  [`&.${tabClasses.root}`]: {
                    minHeight: "36px",
                    paddingBlock: 0,
                    minWidth: "auto",
                    padding: "4px 6px 12px"
                  },
                  ...tabStyle
                }}
                data-testid={`hyro-tab-${label}`}
                label={
                  trimLabel
                    ? (
                      <Tooltip title={label} enterDelay={100} placement="top">
                        <span>{middleEllipsisTrim(label, 10)}</span>
                      </Tooltip>
                    )
                    : (<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span>{label}</span>
                      {mandatory && <MandatoryBadge />}
                    </div>)
                }
                value={value}
                key={value}
                disabled={disabled}
                style={{ minHeight: customTabHeight }}
              />
            ))}
          </Tabs>
          {tabsSideComponent}
        </TabsBox>
        {tabsSpec.map(({ content, label, value, tabStyle }) => (
          activeValue === value ? <TabPanel value={value} key={value} style={{ display: "flex", flexDirection: "column", flexGrow: 1, padding: "24px 0 0 0", ...panelStyle, ...tabStyle }}>
            {content ?? (
              <Paper>
                {label}
                {" "}
                {content}
              </Paper>
            )}
          </TabPanel> : null
        ))}
      </TabContext>
    </>
  );
};

export default HyroTabs;
